import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export interface SyutSearchGridColumn<T = any> {
  /** Unique key for the column */
  key: string;
  /** Display header */
  header: string;
  /** Width of the column (e.g., '100px', '20%') */
  width?: string;
  /** Minimum width */
  minWidth?: string;
  /** Custom cell renderer */
  render?: (value: any, row: T, index: number) => React.ReactNode;
  /** Whether the column is sortable */
  sortable?: boolean;
  /** Whether the column is searchable */
  searchable?: boolean;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
}

export interface SyutSearchGridFilter {
  /** Unique key for the filter */
  key: string;
  /** Display label */
  label: string;
  /** Filter type */
  type: 'text' | 'select' | 'date' | 'number';
  /** Options for select type */
  options?: { value: string; label: string }[];
  /** Placeholder text */
  placeholder?: string;
  /** Default value */
  defaultValue?: string;
}

export interface SyutSearchGridProps<T = any> {
  /** Whether the popup is open */
  isOpen: boolean;
  /** Callback when popup closes */
  onClose: () => void;
  /** Callback when a record is selected */
  onSelect: (record: T) => void;
  /** Title of the popup */
  title?: string;
  /** Column definitions */
  columns: SyutSearchGridColumn<T>[];
  /** Data to display (for non-async mode) */
  data?: T[];
  /** Async function to load data */
  loadData?: (params: {
    searchQuery: string;
    filters: Record<string, string>;
    sortKey?: string;
    sortDirection?: 'asc' | 'desc';
    page: number;
    pageSize: number;
  }) => Promise<{ data: T[]; total: number }>;
  /** Unique key field in the data */
  rowKey: keyof T | ((row: T) => string);
  /** Filter definitions */
  filters?: SyutSearchGridFilter[];
  /** Page size options */
  pageSizeOptions?: number[];
  /** Default page size */
  defaultPageSize?: number;
  /** Enable recently used records */
  enableRecentlyUsed?: boolean;
  /** Storage key for recently used records */
  recentlyUsedKey?: string;
  /** Maximum recently used records to store */
  maxRecentlyUsed?: number;
  /** Debounce delay for search */
  debounceDelay?: number;
  /** Placeholder for search input */
  searchPlaceholder?: string;
  /** Show row numbers */
  showRowNumbers?: boolean;
  /** Custom empty message */
  emptyMessage?: string;
  /** Custom loading message */
  loadingMessage?: string;
  /** Width of the popup */
  width?: string;
  /** Height of the popup */
  height?: string;
  /** Z-index for the popup */
  zIndex?: number;
}

// ============================================================================
// RECENTLY USED STORAGE HELPERS
// ============================================================================

const getRecentlyUsed = <T,>(key: string, max: number): T[] => {
  try {
    const stored = localStorage.getItem(`syut-search-grid-recent-${key}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.slice(0, max) : [];
    }
  } catch (e) {
    console.warn('Failed to load recently used records:', e);
  }
  return [];
};

const saveRecentlyUsed = <T,>(key: string, record: T, rowKeyFn: (row: T) => string, max: number): void => {
  try {
    const current = getRecentlyUsed<T>(key, max);
    const recordKey = rowKeyFn(record);
    const filtered = current.filter(r => rowKeyFn(r) !== recordKey);
    const updated = [record, ...filtered].slice(0, max);
    localStorage.setItem(`syut-search-grid-recent-${key}`, JSON.stringify(updated));
  } catch (e) {
    console.warn('Failed to save recently used record:', e);
  }
};

// ============================================================================
// INJECT STYLES
// ============================================================================

if (typeof document !== 'undefined') {
  const styleId = 'syut-search-grid-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .syut-search-grid-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        animation: fadeIn 0.15s ease-out;
      }

      .syut-search-grid-container {
        background: var(--bg-primary, #ffffff);
        border-radius: 8px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        display: flex;
        flex-direction: column;
        max-height: 90vh;
        max-width: 95vw;
        animation: slideIn 0.2s ease-out;
      }

      .syut-search-grid-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem 1.25rem;
        border-bottom: 1px solid var(--border-color, #e2e8f0);
        background: var(--bg-secondary, #f8fafc);
        border-radius: 8px 8px 0 0;
      }

      .syut-search-grid-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--text-primary, #1e293b);
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .syut-search-grid-close {
        background: none;
        border: none;
        padding: 0.375rem;
        cursor: pointer;
        border-radius: 4px;
        color: var(--text-muted, #64748b);
        transition: all 0.15s ease;
      }

      .syut-search-grid-close:hover {
        background: var(--bg-tertiary, #e2e8f0);
        color: var(--text-primary, #1e293b);
      }

      .syut-search-grid-toolbar {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.75rem 1.25rem;
        border-bottom: 1px solid var(--border-color, #e2e8f0);
        flex-wrap: wrap;
      }

      .syut-search-grid-search {
        flex: 1;
        min-width: 200px;
        position: relative;
      }

      .syut-search-grid-search input {
        width: 100%;
        padding: 0.5rem 0.75rem 0.5rem 2.25rem;
        border: 1px solid var(--border-color, #e2e8f0);
        border-radius: 6px;
        font-size: 0.875rem;
        background: var(--bg-primary, #ffffff);
        color: var(--text-primary, #1e293b);
        transition: all 0.15s ease;
      }

      .syut-search-grid-search input:focus {
        outline: none;
        border-color: var(--primary, #3b82f6);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .syut-search-grid-search-icon {
        position: absolute;
        left: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        color: var(--text-muted, #64748b);
      }

      .syut-search-grid-filters {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: wrap;
      }

      .syut-search-grid-filter {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .syut-search-grid-filter label {
        font-size: 0.75rem;
        color: var(--text-muted, #64748b);
        font-weight: 500;
      }

      .syut-search-grid-filter input,
      .syut-search-grid-filter select {
        padding: 0.375rem 0.5rem;
        border: 1px solid var(--border-color, #e2e8f0);
        border-radius: 4px;
        font-size: 0.8rem;
        min-width: 120px;
        background: var(--bg-primary, #ffffff);
        color: var(--text-primary, #1e293b);
      }

      .syut-search-grid-content {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      .syut-search-grid-tabs {
        display: flex;
        border-bottom: 1px solid var(--border-color, #e2e8f0);
        padding: 0 1.25rem;
        background: var(--bg-secondary, #f8fafc);
      }

      .syut-search-grid-tab {
        padding: 0.625rem 1rem;
        border: none;
        background: none;
        cursor: pointer;
        font-size: 0.875rem;
        color: var(--text-muted, #64748b);
        border-bottom: 2px solid transparent;
        transition: all 0.15s ease;
        margin-bottom: -1px;
      }

      .syut-search-grid-tab:hover {
        color: var(--text-primary, #1e293b);
      }

      .syut-search-grid-tab.active {
        color: var(--primary, #3b82f6);
        border-bottom-color: var(--primary, #3b82f6);
        font-weight: 500;
      }

      .syut-search-grid-grid-container {
        flex: 1;
        overflow: auto;
        padding: 0;
      }

      .syut-search-grid-grid {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.875rem;
      }

      .syut-search-grid-grid th {
        position: sticky;
        top: 0;
        background: var(--bg-secondary, #f8fafc);
        padding: 0.625rem 0.75rem;
        text-align: left;
        font-weight: 600;
        color: var(--text-primary, #1e293b);
        border-bottom: 2px solid var(--border-color, #e2e8f0);
        white-space: nowrap;
        user-select: none;
        z-index: 1;
      }

      .syut-search-grid-grid th.sortable {
        cursor: pointer;
      }

      .syut-search-grid-grid th.sortable:hover {
        background: var(--bg-tertiary, #e2e8f0);
      }

      .syut-search-grid-grid th .sort-icon {
        margin-left: 0.375rem;
        opacity: 0.5;
      }

      .syut-search-grid-grid th .sort-icon.active {
        opacity: 1;
        color: var(--primary, #3b82f6);
      }

      .syut-search-grid-grid td {
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid var(--border-color, #e2e8f0);
        color: var(--text-primary, #1e293b);
      }

      .syut-search-grid-grid tr {
        cursor: pointer;
        transition: background 0.1s ease;
      }

      .syut-search-grid-grid tbody tr:hover {
        background: var(--bg-secondary, #f8fafc);
      }

      .syut-search-grid-grid tbody tr.selected {
        background: rgba(59, 130, 246, 0.1);
      }

      .syut-search-grid-grid tbody tr.highlighted {
        background: rgba(59, 130, 246, 0.15);
        outline: 2px solid var(--primary, #3b82f6);
        outline-offset: -2px;
      }

      .syut-search-grid-grid tbody tr:focus {
        outline: none;
      }

      .syut-search-grid-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        color: var(--text-muted, #64748b);
      }

      .syut-search-grid-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        gap: 0.75rem;
        color: var(--text-muted, #64748b);
      }

      .syut-search-grid-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1.25rem;
        border-top: 1px solid var(--border-color, #e2e8f0);
        background: var(--bg-secondary, #f8fafc);
        border-radius: 0 0 8px 8px;
      }

      .syut-search-grid-pagination {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .syut-search-grid-pagination button {
        padding: 0.375rem 0.625rem;
        border: 1px solid var(--border-color, #e2e8f0);
        background: var(--bg-primary, #ffffff);
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.8rem;
        color: var(--text-primary, #1e293b);
        transition: all 0.15s ease;
      }

      .syut-search-grid-pagination button:hover:not(:disabled) {
        background: var(--bg-secondary, #f8fafc);
        border-color: var(--primary, #3b82f6);
      }

      .syut-search-grid-pagination button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .syut-search-grid-pagination select {
        padding: 0.375rem 0.5rem;
        border: 1px solid var(--border-color, #e2e8f0);
        border-radius: 4px;
        font-size: 0.8rem;
        background: var(--bg-primary, #ffffff);
        color: var(--text-primary, #1e293b);
      }

      .syut-search-grid-info {
        font-size: 0.8rem;
        color: var(--text-muted, #64748b);
      }

      .syut-search-grid-shortcuts {
        display: flex;
        align-items: center;
        gap: 1rem;
        font-size: 0.75rem;
        color: var(--text-muted, #64748b);
      }

      .syut-search-grid-shortcuts kbd {
        padding: 0.125rem 0.375rem;
        background: var(--bg-tertiary, #e2e8f0);
        border-radius: 3px;
        font-family: monospace;
        font-size: 0.7rem;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slideIn {
        from {
          opacity: 0;
          transform: scale(0.95) translateY(-10px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SyutSearchGrid<T extends Record<string, any>>({
  isOpen,
  onClose,
  onSelect,
  title = 'Search',
  columns,
  data: staticData,
  loadData,
  rowKey,
  filters = [],
  pageSizeOptions = [10, 20, 50, 100],
  defaultPageSize = 20,
  enableRecentlyUsed = true,
  recentlyUsedKey = 'default',
  maxRecentlyUsed = 10,
  debounceDelay = 300,
  searchPlaceholder = 'Search... (F2 to focus)',
  showRowNumbers = true,
  emptyMessage = 'No records found',
  loadingMessage = 'Loading...',
  width = '900px',
  height = '600px',
  zIndex = 10000,
}: SyutSearchGridProps<T>) {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [sortKey, setSortKey] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [asyncData, setAsyncData] = useState<T[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeTab, setActiveTab] = useState<'search' | 'recent'>('search');
  const [recentlyUsed, setRecentlyUsed] = useState<T[]>([]);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to get row key
  const getRowKey = useCallback((row: T): string => {
    if (typeof rowKey === 'function') {
      return rowKey(row);
    }
    return String(row[rowKey]);
  }, [rowKey]);

  // Load recently used on mount
  useEffect(() => {
    if (enableRecentlyUsed && isOpen) {
      const recent = getRecentlyUsed<T>(recentlyUsedKey, maxRecentlyUsed);
      setRecentlyUsed(recent);
    }
  }, [enableRecentlyUsed, recentlyUsedKey, maxRecentlyUsed, isOpen]);

  // Filter and sort static data
  const processedStaticData = useMemo(() => {
    if (!staticData) return [];

    let result = [...staticData];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const searchableColumns = columns.filter(col => col.searchable !== false);
      result = result.filter(row =>
        searchableColumns.some(col => {
          const value = row[col.key];
          return value && String(value).toLowerCase().includes(query);
        })
      );
    }

    // Apply filters
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value) {
        result = result.filter(row => {
          const rowValue = row[key];
          return rowValue && String(rowValue).toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    // Apply sort
    if (sortKey) {
      result.sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [staticData, searchQuery, filterValues, sortKey, sortDirection, columns]);

  // Get paginated static data
  const paginatedStaticData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return processedStaticData.slice(start, start + pageSize);
  }, [processedStaticData, currentPage, pageSize]);

  // Determine which data to display
  const displayData = activeTab === 'recent' ? recentlyUsed : (loadData ? asyncData : paginatedStaticData);
  const total = activeTab === 'recent' ? recentlyUsed.length : (loadData ? totalRecords : processedStaticData.length);
  const totalPages = Math.ceil(total / pageSize);

  // Load async data
  const fetchData = useCallback(async () => {
    if (!loadData || activeTab === 'recent') return;

    setIsLoading(true);
    try {
      const result = await loadData({
        searchQuery,
        filters: filterValues,
        sortKey,
        sortDirection,
        page: currentPage,
        pageSize,
      });
      setAsyncData(result.data);
      setTotalRecords(result.total);
    } catch (error) {
      console.error('Failed to load data:', error);
      setAsyncData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [loadData, searchQuery, filterValues, sortKey, sortDirection, currentPage, pageSize, activeTab]);

  // Debounced fetch for async mode
  useEffect(() => {
    if (!loadData || activeTab === 'recent') return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchData();
    }, debounceDelay);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [fetchData, debounceDelay, activeTab]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(-1);
      setHighlightedIndex(-1);
      setCurrentPage(1);
      setSearchQuery('');
      setFilterValues({});
      setActiveTab('search');
    }
  }, [isOpen]);

  // Handle record selection
  const handleSelect = useCallback((record: T) => {
    if (enableRecentlyUsed) {
      saveRecentlyUsed(recentlyUsedKey, record, getRowKey, maxRecentlyUsed);
    }
    onSelect(record);
    onClose();
  }, [enableRecentlyUsed, recentlyUsedKey, getRowKey, maxRecentlyUsed, onSelect, onClose]);

  // Handle sort
  const handleSort = useCallback((key: string) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  }, [sortKey]);

  // Scroll highlighted row into view
  useEffect(() => {
    if (highlightedIndex >= 0 && gridContainerRef.current) {
      const rows = gridContainerRef.current.querySelectorAll('tbody tr');
      const row = rows[highlightedIndex] as HTMLElement;
      if (row) {
        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const dataLength = displayData.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const next = prev + 1;
          return next >= dataLength ? 0 : next;
        });
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const next = prev - 1;
          return next < 0 ? dataLength - 1 : next;
        });
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < dataLength) {
          handleSelect(displayData[highlightedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        onClose();
        break;

      case 'F2':
        e.preventDefault();
        searchInputRef.current?.focus();
        break;

      case 'PageDown':
        e.preventDefault();
        if (currentPage < totalPages) {
          setCurrentPage(prev => prev + 1);
          setHighlightedIndex(0);
        }
        break;

      case 'PageUp':
        e.preventDefault();
        if (currentPage > 1) {
          setCurrentPage(prev => prev - 1);
          setHighlightedIndex(0);
        }
        break;

      case 'Home':
        if (e.ctrlKey) {
          e.preventDefault();
          setCurrentPage(1);
          setHighlightedIndex(0);
        }
        break;

      case 'End':
        if (e.ctrlKey) {
          e.preventDefault();
          setCurrentPage(totalPages);
          setHighlightedIndex(0);
        }
        break;
    }
  }, [displayData, highlightedIndex, handleSelect, onClose, currentPage, totalPages]);

  // Global keyboard shortcut listener
  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // F2 to focus search
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // Ctrl+L to focus search (alternative)
      if (e.ctrlKey && e.key === 'l') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="syut-search-grid-backdrop"
      style={{ zIndex }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        className="syut-search-grid-container"
        style={{ width, height }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="syut-search-grid-title"
      >
        {/* Header */}
        <div className="syut-search-grid-header">
          <h2 id="syut-search-grid-title" className="syut-search-grid-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            {title}
          </h2>
          <button
            className="syut-search-grid-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="syut-search-grid-toolbar">
          <div className="syut-search-grid-search">
            <svg className="syut-search-grid-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
                setHighlightedIndex(-1);
              }}
              placeholder={searchPlaceholder}
              aria-label="Search"
            />
          </div>

          {filters.length > 0 && (
            <div className="syut-search-grid-filters">
              {filters.map(filter => (
                <div key={filter.key} className="syut-search-grid-filter">
                  <label>{filter.label}</label>
                  {filter.type === 'select' ? (
                    <select
                      value={filterValues[filter.key] || ''}
                      onChange={(e) => {
                        setFilterValues(prev => ({ ...prev, [filter.key]: e.target.value }));
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All</option>
                      {filter.options?.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={filter.type}
                      value={filterValues[filter.key] || ''}
                      onChange={(e) => {
                        setFilterValues(prev => ({ ...prev, [filter.key]: e.target.value }));
                        setCurrentPage(1);
                      }}
                      placeholder={filter.placeholder}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        {enableRecentlyUsed && (
          <div className="syut-search-grid-tabs">
            <button
              className={`syut-search-grid-tab ${activeTab === 'search' ? 'active' : ''}`}
              onClick={() => setActiveTab('search')}
            >
              Search Results
            </button>
            <button
              className={`syut-search-grid-tab ${activeTab === 'recent' ? 'active' : ''}`}
              onClick={() => setActiveTab('recent')}
            >
              Recently Used ({recentlyUsed.length})
            </button>
          </div>
        )}

        {/* Content */}
        <div className="syut-search-grid-content">
          <div className="syut-search-grid-grid-container" ref={gridContainerRef}>
            {isLoading ? (
              <div className="syut-search-grid-loading">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ animation: 'spin 1s linear infinite' }}
                >
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                </svg>
                {loadingMessage}
              </div>
            ) : displayData.length === 0 ? (
              <div className="syut-search-grid-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.5 }}>
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <p style={{ marginTop: '1rem' }}>{emptyMessage}</p>
              </div>
            ) : (
              <table className="syut-search-grid-grid">
                <thead>
                  <tr>
                    {showRowNumbers && <th style={{ width: '50px', textAlign: 'center' }}>#</th>}
                    {columns.map(col => (
                      <th
                        key={col.key}
                        className={col.sortable !== false ? 'sortable' : ''}
                        style={{
                          width: col.width,
                          minWidth: col.minWidth,
                          textAlign: col.align || 'left',
                        }}
                        onClick={() => col.sortable !== false && handleSort(col.key)}
                      >
                        {col.header}
                        {col.sortable !== false && (
                          <span className={`sort-icon ${sortKey === col.key ? 'active' : ''}`}>
                            {sortKey === col.key ? (
                              sortDirection === 'asc' ? '↑' : '↓'
                            ) : '↕'}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayData.map((row, index) => {
                    const rowKeyValue = getRowKey(row);
                    const isHighlighted = index === highlightedIndex;
                    const isSelected = index === selectedIndex;
                    const rowNumber = activeTab === 'recent' ? index + 1 : (currentPage - 1) * pageSize + index + 1;

                    return (
                      <tr
                        key={rowKeyValue}
                        className={`${isHighlighted ? 'highlighted' : ''} ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelect(row)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onDoubleClick={() => handleSelect(row)}
                        tabIndex={0}
                      >
                        {showRowNumbers && (
                          <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                            {rowNumber}
                          </td>
                        )}
                        {columns.map(col => (
                          <td
                            key={col.key}
                            style={{ textAlign: col.align || 'left' }}
                          >
                            {col.render
                              ? col.render(row[col.key], row, index)
                              : row[col.key]
                            }
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="syut-search-grid-footer">
          <div className="syut-search-grid-info">
            {activeTab === 'search' ? (
              <>
                Showing {displayData.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} - {Math.min(currentPage * pageSize, total)} of {total} records
              </>
            ) : (
              <>
                {recentlyUsed.length} recently used record{recentlyUsed.length !== 1 ? 's' : ''}
              </>
            )}
          </div>

          <div className="syut-search-grid-shortcuts">
            <span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
            <span><kbd>Enter</kbd> Select</span>
            <span><kbd>F2</kbd> Search</span>
            <span><kbd>Esc</kbd> Close</span>
          </div>

          {activeTab === 'search' && (
            <div className="syut-search-grid-pagination">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                title="First page"
              >
                ««
              </button>
              <button
                onClick={() => setCurrentPage(prev => prev - 1)}
                disabled={currentPage === 1}
                title="Previous page"
              >
                «
              </button>
              <span style={{ padding: '0 0.5rem', fontSize: '0.8rem' }}>
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={currentPage >= totalPages}
                title="Next page"
              >
                »
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage >= totalPages}
                title="Last page"
              >
                »»
              </button>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                title="Page size"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SyutSearchGrid;
